// Copyright ©️ 2026 eVolve MEP, LLC

import './catalog.css';

import { useCallback, useState, useRef } from 'react';

import {
  type PartAttributeDefinitionVariantId,
  type PartAttributeDefinition,
  useEvolvePost,
  useEvolveGet,
} from '@evolvemep/api-ts';
import { ActionIcon, Flex, InputLabel } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { WrappedMultiSelect } from 'components/Mantine/TypeSafeSelect/WrappedMultiSelect';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  name: string;
  allowWriteInVariant: boolean;
  onRemove?: () => void;
  disabled?: boolean;
  editable?: boolean;
} & (
  | {
      value?: string[];
      onChange: (variants: string[]) => void;
    }
  | {
      value?: PartAttributeDefinitionVariantId[];
      onChange: (variants: PartAttributeDefinitionVariantId[]) => void;
      partAttributeDefinition: PartAttributeDefinition;
      onPartAttributeDefinitionUpdated: (partAttributeDefinition: PartAttributeDefinition) => void;
    }
);

export const AttributeLabelComponent = ({
  allowWriteInVariant,
  name,
  global,
  onRemove,
  editable,
  disabled,
}: Pick<Props, 'allowWriteInVariant' | 'name' | 'onRemove' | 'editable' | 'disabled'> & {
  global?: boolean;
}) => (
  <Flex align="center" justify="space-between" gap="xs">
    <InputLabel>{name}</InputLabel>
    <Flex gap="xs" align="center" className="hover-title--on">
      {global && (
        <AgGridStyleTooltip label="Global attribute" withArrow openDelay={200}>
          <EvolveIcon icon="External" color="gray" size="sm" />
        </AgGridStyleTooltip>
      )}
      {allowWriteInVariant && (
        <AgGridStyleTooltip label="Write-in values allowed" withArrow openDelay={200}>
          <EvolveIcon icon="WriteInValue" color="gray" size="sm" />
        </AgGridStyleTooltip>
      )}
      {editable ? (
        isNotNil(onRemove) && (
          <AgGridStyleTooltip label="Remove attribute" withArrow openDelay={200}>
            <ActionIcon size="xs" color="red" onClick={onRemove} disabled={disabled}>
              <EvolveIcon icon="Delete" size="sm" />
            </ActionIcon>
          </AgGridStyleTooltip>
        )
      ) : (
        <AgGridStyleTooltip label="Read-only attribute" withArrow openDelay={200}>
          <EvolveIcon icon="AccessLock" size="sm" color="gray" />
        </AgGridStyleTooltip>
      )}
    </Flex>
  </Flex>
);

export const AttributeWithVariants = (props: Props) => {
  const attributeFetchedRef = useRef(false);
  const { apiCall: getGlobalAttribute, loading: loadingAttribute } = useEvolveGet(
    `shop/partCatalog/globalAttribute/:partAttributeDefinitionId`,
    {
      alertOnError: true,
    },
  );
  const { apiCall: addAttributeVariant, loading: saving } = useEvolvePost(
    `shop/partCatalog/globalAttribute/:partAttributeDefinitionId/variant`,
  );

  const onNewGlobalVariantAdded = useCallback(
    (newVariantName: string) => {
      if (!('partAttributeDefinition' in props)) return;
      const { partAttributeDefinitionId, variants } = props.partAttributeDefinition;
      void addAttributeVariant(
        { name: newVariantName },
        { url: `shop/partCatalog/globalAttribute/${partAttributeDefinitionId}/variant` },
      ).then((newVariant) => {
        props.onPartAttributeDefinitionUpdated({
          ...props.partAttributeDefinition,
          variants: [...variants, newVariant],
        });
        props.onChange([...(props.value ?? []), newVariant.partAttributeDefinitionVariantId]);
      });
    },
    [addAttributeVariant, props],
  );

  const [searchValue, setSearchValue] = useState('');

  const loadAttribute = useCallback(async () => {
    if (attributeFetchedRef.current || !('partAttributeDefinition' in props)) return;
    attributeFetchedRef.current = true;
    await getGlobalAttribute({
      url: `shop/partCatalog/globalAttribute/${props.partAttributeDefinition.partAttributeDefinitionId}`,
    })
      .then(props.onPartAttributeDefinitionUpdated)
      .catch(() => {
        attributeFetchedRef.current = false;
      });
  }, [getGlobalAttribute, props]);

  return (
    <Flex justify="space-between" w="100%" gap="xs" className="hover-title">
      {'partAttributeDefinition' in props ? (
        <WrappedMultiSelect
          className="full-width-label"
          label={<AttributeLabelComponent global {...props} />}
          onChange={props.onChange}
          value={props.value}
          data={props.partAttributeDefinition.variants.map((v) => ({
            value: v.partAttributeDefinitionVariantId,
            label: v.name,
          }))}
          onFocus={loadAttribute}
          loading={saving || loadingAttribute}
          disabled={props.disabled}
          creatable={props.editable}
          createLabel="Create new variant"
          onCreate={onNewGlobalVariantAdded}
          nothingFoundMessage={props.editable ? undefined : 'All variants selected'}
          clearSearchOnChange
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          w="100%"
        />
      ) : (
        <WrappedMultiSelect
          className="full-width-label"
          label={<AttributeLabelComponent {...props} />}
          onChange={props.onChange}
          value={props.value}
          data={props.value ?? []}
          disabled={props.disabled}
          creatable={props.editable}
          onCreate={(v) => {
            props.onChange([...(props.value ?? []), v]);
            setSearchValue('');
          }}
          createLabel="Create new variant"
          nothingFoundMessage={props.editable ? undefined : 'All variants selected'}
          clearSearchOnChange
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          w="100%"
        />
      )}
    </Flex>
  );
};
